import { useCallback, useEffect, useMemo, useState } from "react";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Plus, Search, Trash2, X } from "lucide-react";
import { type Dispatch, type UnknownAction } from "redux";

import {
  deleteExchangeLocationsDetails as deleteExchangeLocationsDetailsAction,
  listExchangeLocations as listExchangeLocationsAction,
  listExchangeLocationsReset as listExchangeLocationsResetAction,
  listExchangeLocationTypes as listExchangeLocationTypesAction,
  listExchangeLocationTypesReset as listExchangeLocationTypesResetAction,
  listTraders as listTradersAction,
  listTradersReset as listTradersResetAction,
  treeAdministrativeAreas as treeAdministrativeAreasAction,
} from "@/actions";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SortMenu } from "@/components/SortMenu";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { ALL_ITEMS_PAGE_SIZE } from "@/constants/pagination";
import { getCountryObjectFromCache } from "@/utils";
import { adaptAdministrativeAreas } from "@/utils/adapters";

import { DEFAULT_SORTING, EXCHANGE_LOCATION_SORT_OPTIONS, List } from "./components/List";
import { ConnectedExchangeLocationsEditModal } from "./components/Modal";
import {
  type AdministrativeAreaNode,
  type EntityId,
  type ExchangeLocation,
  type ExchangeLocationModalOptions,
  type ExchangeLocationModalType,
  type ExchangeLocationType,
  type TraderOrganization,
} from "./types";
import { flattenAdministrativeAreas } from "./utils";

type ListResponse<TItem> = TItem[] | { results?: TItem[] };

interface ListState<TItem> {
  data: ListResponse<TItem>;
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  listExchangeLocations: ListState<ExchangeLocation>;
  listExchangeLocationTypes: ListState<ExchangeLocationType>;
  listTraders: ListState<TraderOrganization>;
  treeAdministrativeAreas: {
    data: { administrative_areas?: AdministrativeAreaNode[] };
  };
}

interface StateProps {
  administrativeAreas: AdministrativeAreaNode[];
  errorMessage: string;
  exchangeLocationTypes: ExchangeLocationType[];
  exchangeLocations: ExchangeLocation[];
  isFetching: boolean;
  traders: TraderOrganization[];
}

interface DispatchProps {
  deleteExchangeLocationsDetails: (id: EntityId) => void;
  listExchangeLocationTypes: () => void;
  listExchangeLocationTypesReset: () => void;
  listExchangeLocations: () => void;
  listExchangeLocationsReset: () => void;
  listTraders: () => void;
  listTradersReset: () => void;
  treeAdministrativeAreas: (countryId: EntityId) => void;
}

type WfpManagerExchangeLocationsViewProps = StateProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const CLOSED_MODAL_OPTIONS: ExchangeLocationModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const getListResults = <TItem,>(data: ListResponse<TItem>): TItem[] =>
  Array.isArray(data) ? data : (data?.results ?? []);

export const WfpManagerExchangeLocationsView = ({
  administrativeAreas,
  deleteExchangeLocationsDetails,
  errorMessage,
  exchangeLocationTypes,
  exchangeLocations,
  isFetching,
  listExchangeLocationTypes,
  listExchangeLocationTypesReset,
  listExchangeLocations,
  listExchangeLocationsReset,
  listTraders,
  listTradersReset,
  traders,
  treeAdministrativeAreas,
}: WfpManagerExchangeLocationsViewProps) => {
  const { t } = useTranslation();
  const [modalOptions, setModalOptions] =
    useState<ExchangeLocationModalOptions>(CLOSED_MODAL_OPTIONS);
  const [locationToDelete, setLocationToDelete] = useState<ExchangeLocation | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const selectedSort = sorting[0];
  const selectedSortColumn =
    EXCHANGE_LOCATION_SORT_OPTIONS.find(sortOption => sortOption.id === selectedSort?.id)?.id || "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  const areaOptions = useMemo(
    () => flattenAdministrativeAreas(administrativeAreas),
    [administrativeAreas]
  );

  useEffect(() => {
    listExchangeLocationTypes();
    listExchangeLocations();
    listTraders();

    const country = getCountryObjectFromCache();
    if (country?.id) {
      treeAdministrativeAreas(country.id);
    }

    return () => {
      listTradersReset();
      listExchangeLocationsReset();
      listExchangeLocationTypesReset();
    };
  }, [
    listExchangeLocationTypes,
    listExchangeLocationTypesReset,
    listExchangeLocations,
    listExchangeLocationsReset,
    listTraders,
    listTradersReset,
    treeAdministrativeAreas,
  ]);

  const onClickAdd = () => {
    setModalOptions({ id: "add", isOpen: true, type: "create" });
  };

  const onOpenModal = useCallback(
    (exchangeLocation: ExchangeLocation, type: ExchangeLocationModalType) => {
      setModalOptions({ id: exchangeLocation.id ?? undefined, isOpen: true, type });
    },
    []
  );

  const onConfirmDelete = () => {
    if (locationToDelete?.id !== undefined && locationToDelete?.id !== null) {
      deleteExchangeLocationsDetails(locationToDelete.id);
    }

    setLocationToDelete(null);
  };

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || EXCHANGE_LOCATION_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  return (
    <>
      <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
        <div className="flex flex-col gap-4">
          <PageBreadcrumb
            items={[
              { label: t("header.configuration"), href: "/wfpManager/configuration" },
              { label: t("configuration.item.exchangeLocations") },
            ]}
          />
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-y-1">
              <h1 className="font-semibold text-2xl">
                {t("configuration.item.exchangeLocations")}
              </h1>
              <p className="text-muted-foreground">{t("exchangeLocations.subtitle")}</p>
            </div>
            <WFPButton type="button" onClick={onClickAdd}>
              <Plus className="size-4" aria-hidden="true" />
              {t("viewExchangeDetailsModal.addNewExchangeLocation")}
            </WFPButton>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <InputGroup className="w-full max-w-xs sm:w-80">
            <InputGroupAddon>
              <Search className="size-4" aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              id="exchange-locations-search-input"
              type="search"
              value={searchTerm}
              className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              placeholder={t("exchangeLocations.list.searchPlaceholder", {
                defaultValue: "Search by location name or area",
              })}
              aria-label={t("exchangeLocations.list.searchPlaceholder", {
                defaultValue: "Search by location name or area",
              })}
              onChange={event => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={t("commodities.clearFilter")}
                  onClick={() => setSearchTerm("")}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
          <SortMenu
            options={EXCHANGE_LOCATION_SORT_OPTIONS}
            sortColumn={selectedSortColumn}
            sortDirection={selectedSortDirection}
            onSortColumnChange={onSortColumnChange}
            onSortDirectionChange={onSortDirectionChange}
          />
        </div>
        <List
          areaOptions={areaOptions}
          errorMessage={errorMessage}
          exchangeLocationTypes={exchangeLocationTypes}
          exchangeLocations={exchangeLocations}
          isFetching={isFetching}
          searchTerm={searchTerm}
          sorting={sorting}
          onClickDelete={setLocationToDelete}
          onOpenModal={onOpenModal}
          onSortingChange={onSortingChange}
        />
      </main>
      <ConnectedExchangeLocationsEditModal
        areaOptions={areaOptions}
        exchangeLocationTypes={exchangeLocationTypes}
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
        traders={traders}
      />
      <Dialog
        open={Boolean(locationToDelete)}
        onOpenChange={open => (open ? undefined : setLocationToDelete(null))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-danger-50 text-danger-600">
                <Trash2 className="size-5" aria-hidden="true" />
              </div>
              <div className="space-y-2">
                <DialogTitle>{t("configuration.item.exchangeLocations")}</DialogTitle>
                <DialogDescription className="whitespace-pre-line text-foreground">
                  {`${t("configuration.common.deleteText")}${locationToDelete?.name ?? ""}`}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <WFPButton type="button" variant="outline" onClick={() => setLocationToDelete(null)}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="button" variant="destructive" onClick={onConfirmDelete}>
              {t("common.delete")}
            </WFPButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    administrativeAreas: adaptAdministrativeAreas(
      state.treeAdministrativeAreas.data.administrative_areas
    ),
    errorMessage:
      state.listExchangeLocations.errorMessage || state.listExchangeLocationTypes.errorMessage,
    exchangeLocationTypes: getListResults(state.listExchangeLocationTypes.data),
    exchangeLocations: getListResults(state.listExchangeLocations.data),
    isFetching:
      state.listExchangeLocations.isFetching || state.listExchangeLocationTypes.isFetching,
    traders: getListResults(state.listTraders.data),
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    deleteExchangeLocationsDetails: id =>
      dispatchAction(
        dispatch,
        deleteExchangeLocationsDetailsAction(id, true, { page_size: ALL_ITEMS_PAGE_SIZE })
      ),
    listExchangeLocationTypes: () =>
      dispatchAction(dispatch, listExchangeLocationTypesAction({ page_size: ALL_ITEMS_PAGE_SIZE })),
    listExchangeLocationTypesReset: () =>
      dispatchAction(dispatch, listExchangeLocationTypesResetAction()),
    listExchangeLocations: () =>
      dispatchAction(dispatch, listExchangeLocationsAction({ page_size: ALL_ITEMS_PAGE_SIZE })),
    listExchangeLocationsReset: () => dispatchAction(dispatch, listExchangeLocationsResetAction()),
    listTraders: () =>
      dispatchAction(dispatch, listTradersAction({ page_size: ALL_ITEMS_PAGE_SIZE })),
    listTradersReset: () => dispatchAction(dispatch, listTradersResetAction()),
    treeAdministrativeAreas: countryId =>
      dispatchAction(
        dispatch,
        treeAdministrativeAreasAction({}, `${countryId}/administrative_areas/`)
      ),
  };
};

export const ConnectedWfpManagerExchangeLocationsView = connect(
  mapStateToProps,
  mapDispatchToProps
)(WfpManagerExchangeLocationsView);
